import { List, Stack } from '@mui/material';
import { useMemo } from 'react';

import { LockUnlocked01Icon } from '@/components/icons/LockUnlocked01Icon';
import { UnstyledNavLink } from '@/components/navigation/UnstyledNavLink';
import { useMatchingRoutes } from '@/hooks/useMatchingRoutes';
import { useSidebarExpansionState } from '@/hooks/useSidebarExpansionState';
import { FeatureAnnouncer } from '@/modules/featureAnnouncements/components/FeatureAnnouncer/FeatureAnnouncer';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { useCanAccessPage } from '@/navigation/usePageAccessLookup';
import { useActiveMenuItem } from '@/utils/navigation/activeMenu';

import { SidebarLogo } from './components/SidebarLogo';
import { GroupedSidebarMenu } from './GroupedSidebarMenu';
import { HelpMenuItem } from './HelpMenuItem';
import {
  CALCULATOR_MENU_LABEL,
  TASKS_MENU_LABEL,
  useMenuItems,
} from './HouseholdSidebar.utils';
import { MenuItem } from './MenuItem';
import { SidebarWithContainer } from './Sidebar';
import { SidebarCollapseToggle } from './SidebarCollapseToggle';
import { menuItemSxProps } from './sidebarConstants';
import { SidebarFillLayout } from './SidebarFillLayout';

export const MENU_ITEMS_ID = 'sidebar-menu-items';

export const HOUSEHOLD_SIDEBAR_WIDTH = 167;

interface HouseholdSidebarProps {
  householdId?: string; // We do this to satisfy types, but householdId must always be present for this component to render.
}

export function HouseholdSidebar({ householdId }: HouseholdSidebarProps) {
  if (!householdId) {
    throw new Error('householdId is required');
  }

  const { expansionState, isExpanded, toggle, isTooltipReady } =
    useSidebarExpansionState();

  const menuItems = useMenuItems({ householdId, isExpanded });
  const colorTheme = 'light';

  const showAccessRoute = useCanAccessPage(ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ACCESS);

  const accessRoute = useMemo(() => {
    if (!householdId) return undefined;

    return getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ACCESS, {
      householdId,
    });
  }, [householdId]);

  const { isMatchingRoute: isOnImplementationPage } = useMatchingRoutes([
    ROUTE_KEYS.HOUSEHOLD_ENTITY_IMPLEMENTATION,
  ]);

  const { isMatchingRoute: isOnCalculatorPage } = useMatchingRoutes([
    ROUTE_KEYS.HOUSEHOLD_GIFT_DESIGNER,
    ROUTE_KEYS.HOUSEHOLD_GIFT_DESIGNER_BASIC_INFORMATION,
    ROUTE_KEYS.HOUSEHOLD_GIFT_DESIGNER_MODEL_SCENARIOS,

    ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER,
    ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION,
    ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_MODEL_SCENARIOS,

    ROUTE_KEYS.HOUSEHOLD_PROPOSAL_CREATE,
    ROUTE_KEYS.HOUSEHOLD_PROPOSAL_EDIT,
  ]);
  const { isMatchingRoute: isOnAccessPage } = useMatchingRoutes([
    ROUTE_KEYS.MANAGE_HOUSEHOLD_DETAILS,
    ROUTE_KEYS.HOUSEHOLD_ACTIVITY_HISTORY,
    ROUTE_KEYS.MANAGE_HOUSEHOLD_ADMIN,
  ]);

  const accessRouteItem = accessRoute ? { to: accessRoute } : undefined;
  const { active, prev, fallback } = useActiveMenuItem(
    menuItems,
    accessRouteItem
  );

  const activeSidebarItem = useMemo(() => {
    let activeItem = active;

    if (isOnImplementationPage && !activeItem) {
      activeItem = menuItems.find((item) => item.text === TASKS_MENU_LABEL);
    }

    if (isOnCalculatorPage && !activeItem) {
      activeItem = menuItems.find(
        (item) => item.text === CALCULATOR_MENU_LABEL
      );
    }

    // Apply fallback logic after special page handling
    return activeItem ?? prev ?? fallback;
  }, [
    active,
    prev,
    fallback,
    isOnImplementationPage,
    isOnCalculatorPage,
    menuItems,
  ]);

  return (
    <SidebarWithContainer
      expansionState={expansionState}
      data-it="Sidebar"
      width={HOUSEHOLD_SIDEBAR_WIDTH}
      collapsedWidth={56}
    >
      <Stack
        display="flex"
        direction="column"
        justifyContent="space-between"
        alignItems="center"
        height="100%"
        overflow="auto"
        sx={{
          // Hide scrollbar for Chrome, Safari and Opera
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          // Hide scrollbar for Firefox
          scrollbarWidth: 'none',
          // Hide scrollbar for IE and Edge
          msOverflowStyle: 'none',
        }}
      >
        <Stack width="100%" minHeight="100%" pb={1} spacing={2}>
          <UnstyledNavLink
            to={getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLDS_LIST, {})}
          >
            <SidebarLogo isExpanded={isExpanded} />
          </UnstyledNavLink>
          <List
            sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}
            id={MENU_ITEMS_ID}
          >
            <SidebarFillLayout
              isExpanded={isExpanded}
              onCollapseAreaClick={toggle}
              footer={
                <Stack>
                  <FeatureAnnouncer
                    isExpanded={isExpanded}
                    householdId={householdId}
                  />
                  <SidebarCollapseToggle
                    isExpanded={isExpanded}
                    isTooltipReady={isTooltipReady()}
                    onToggle={toggle}
                    menuItemSxProps={menuItemSxProps}
                    colorTheme={colorTheme}
                  />
                  {showAccessRoute && (
                    <MenuItem
                      text="Access"
                      icon={<LockUnlocked01Icon size={16} />}
                      to={accessRoute}
                      isActive={isOnAccessPage}
                      tooltipTitle="Access"
                      variant={isExpanded ? 'large' : 'small'}
                      menuItemSxProps={menuItemSxProps}
                      colorTheme={colorTheme}
                      typographyProps={{ variant: 'body-xs' }}
                      showTooltip={!isExpanded}
                    />
                  )}
                  <HelpMenuItem
                    isExpanded={isExpanded}
                    colorTheme={colorTheme}
                  />
                </Stack>
              }
            >
              <GroupedSidebarMenu
                items={menuItems}
                isExpanded={isExpanded}
                activeTo={activeSidebarItem?.to}
                commonItemProps={{ colorTheme }}
              />
            </SidebarFillLayout>
          </List>
        </Stack>
      </Stack>
    </SidebarWithContainer>
  );
}
